import { useState, useEffect, useRef } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/lib/auth-store';
import type { EPCR, Attachment } from '@/types';
import { cn } from '@/lib/cn';
import {
  Paperclip, Upload, FileText, Image as ImageIcon,
  Trash2, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';

interface Props {
  epcr: EPCR;
}

const CATEGORIES: { value: Attachment['category']; label: string; accept: string; description: string }[] = [
  { value: '12-lead-ecg', label: '12-Lead ECG', accept: 'image/png,image/jpeg,application/pdf', description: 'ECG tracing from LifePak/ZOLL or photo of printout' },
  { value: 'etco2-waveform', label: 'EtCO2 Waveform', accept: 'image/png,image/jpeg,application/pdf', description: 'Capnography waveform confirming airway placement' },
  { value: 'refusal-signature', label: 'Refusal Form', accept: 'image/png,image/jpeg,application/pdf', description: 'Signed paper refusal form (if not captured digitally)' },
  { value: 'receiving-signature', label: 'Receiving Facility', accept: 'image/png,image/jpeg,application/pdf', description: 'Paper acknowledgment from receiving facility' },
  { value: 'photo', label: 'Scene/Injury Photo', accept: 'image/png,image/jpeg', description: 'Injury, mechanism of injury, or scene photograph' },
  { value: 'other', label: 'Other', accept: '*/*', description: 'Any other supporting document' },
];

export function AttachmentsTab({ epcr }: Props) {
  const { user } = useAuthStore();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Attachment['category']>('12-lead-ecg');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadAttachments();
  }, [epcr.id]);

  async function loadAttachments() {
    const records = await db.attachments.where('epcr_id').equals(epcr.id).toArray();
    setAttachments(records);
  }

  async function handleFile(file: File) {
    if (!user) return;
    setUploading(true);
    setError(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const timestamp = Date.now();
      const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${epcr.agency_id}/${epcr.id}/${timestamp}_${safeFilename}`;

      // Only attempt remote upload if Supabase is configured and online
      let uploadedToStorage = false;
      if (supabaseUrl && navigator.onLine) {
        const { error: uploadErr } = await supabase.storage
          .from('ephi-attachments')
          .upload(storagePath, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type,
          });
        if (uploadErr) {
          // Storage failed — fall through to offline queue
          console.warn('[attachments] storage upload failed, queuing for later:', uploadErr);
        } else {
          uploadedToStorage = true;
        }
      }

      const record: Attachment = {
        id: crypto.randomUUID(),
        epcr_id: epcr.id,
        incident_id: null,
        fire_report_id: null,
        agency_id: epcr.agency_id,
        category: selectedCategory,
        filename: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        storage_path: storagePath,
        storage_bucket: 'ephi-attachments',
        caption: null,
        taken_at: null,
        uploaded_by: user.id,
        created_at: new Date().toISOString(),
      };

      await db.attachments.add(withSyncMeta(record, uploadedToStorage));
      await enqueueSync('attachments', record.id, 'insert', record as unknown as Record<string, unknown>);

      // If the binary itself didn't make it to Storage (offline / 5xx
      // / etc.), the row exists with `_synced: false` but no actual
      // file behind it. Surface a clear "online required to upload
      // file" banner so the medic re-attempts the upload once back
      // online — without this it looks like the attachment landed.
      // Finding 83.
      if (!uploadedToStorage) {
        setError('Saved locally — re-upload required once online so the file reaches the server.');
      }

      await loadAttachments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function remove(att: Attachment) {
    if (!confirm(`Remove ${att.filename}?`)) return;
    try {
      if (navigator.onLine) {
        await supabase.storage.from(att.storage_bucket).remove([att.storage_path]).catch(() => {/* ignore */});
      }
      await db.attachments.delete(att.id);
      await enqueueSync('attachments', att.id, 'delete', { id: att.id });
      await loadAttachments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function getPreviewUrl(att: Attachment): Promise<string | null> {
    if (!navigator.onLine || !import.meta.env.VITE_SUPABASE_URL) return null;
    const { data } = await supabase.storage
      .from(att.storage_bucket)
      .createSignedUrl(att.storage_path, 300); // 5 min TTL
    return data?.signedUrl ?? null;
  }

  const selectedCategoryConfig = CATEGORIES.find(c => c.value === selectedCategory)!;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Paperclip size={20} className="text-brand-400" />
        <h3 className="text-lg font-semibold text-slate-200">Attachments</h3>
        <span className="ml-2 text-xs text-slate-500">
          ({attachments.length} file{attachments.length !== 1 ? 's' : ''})
        </span>
      </div>

      <p className="text-xs text-slate-400">
        Upload ECG tracings, EtCO2 waveforms, signed forms, or supporting photos. All files are stored
        encrypted in Supabase Storage and linked to this report.
      </p>

      {/* Upload form */}
      {!epcr.locked && (
        <section className="card space-y-3">
          <h4 className="font-semibold text-slate-200">Upload New Attachment</h4>
          <div>
            <label className="text-xs text-slate-400">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as Attachment['category'])}
              className="input-field"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">{selectedCategoryConfig.description}</p>
          </div>

          <div
            className={cn(
              'rounded-lg border-2 border-dashed p-6 text-center transition-colors',
              uploading ? 'border-brand-500 bg-brand-500/10' : 'border-slate-600 bg-slate-800/50 hover:bg-slate-800',
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={selectedCategoryConfig.accept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
              className="hidden"
              id="attachment-upload"
            />
            <label htmlFor="attachment-upload" className="cursor-pointer">
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-brand-400">
                  <Loader2 size={20} className="animate-spin" />
                  <span>Uploading...</span>
                </div>
              ) : (
                <>
                  <Upload size={32} className="mx-auto mb-2 text-slate-500" />
                  <div className="text-sm font-medium text-slate-200">Tap to choose file</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Images up to 50MB. Accepted: {selectedCategoryConfig.accept.replace(/\//g, ', ')}
                  </div>
                </>
              )}
            </label>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-950 p-2 text-xs text-red-400">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {!navigator.onLine && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-800 bg-amber-950 p-2 text-xs text-amber-400">
              <AlertCircle size={14} />
              Offline — file upload will fail. Reconnect to save attachments.
            </div>
          )}
        </section>
      )}

      {/* Existing attachments, grouped by category */}
      {CATEGORIES.map((cat) => {
        const items = attachments.filter(a => a.category === cat.value);
        if (items.length === 0) return null;
        return (
          <section key={cat.value} className="card">
            <h4 className="font-semibold text-slate-200 mb-3">{cat.label}</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              {items.map((att) => (
                <AttachmentCard
                  key={att.id}
                  att={att}
                  getPreviewUrl={getPreviewUrl}
                  onRemove={() => remove(att)}
                  canRemove={!epcr.locked}
                />
              ))}
            </div>
          </section>
        );
      })}

      {attachments.length === 0 && (
        <div className="rounded-lg border border-slate-700 bg-slate-900 py-8 text-center">
          <Paperclip size={32} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-500">No attachments yet</p>
        </div>
      )}
    </div>
  );
}

function AttachmentCard({
  att, getPreviewUrl, onRemove, canRemove,
}: {
  att: Attachment;
  getPreviewUrl: (att: Attachment) => Promise<string | null>;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (att.mime_type.startsWith('image/')) {
      getPreviewUrl(att).then(setPreviewUrl);
    }
  }, [att.id]);

  const isImage = att.mime_type.startsWith('image/');
  const sizeKB = Math.round(att.size_bytes / 1024);

  return (
    <div className="flex gap-3 rounded-lg border border-slate-700 bg-slate-800 p-3">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-900 overflow-hidden">
        {isImage && previewUrl ? (
          <img src={previewUrl} alt={att.filename} className="h-full w-full object-cover" />
        ) : isImage ? (
          <ImageIcon size={24} className="text-slate-500" />
        ) : (
          <FileText size={24} className="text-slate-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm font-medium text-slate-200">{att.filename}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
          <span>{sizeKB} KB</span>
          <span>·</span>
          <span>{new Date(att.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 text-xs text-brand-400 hover:text-brand-300"
          >
            Open in new tab
          </a>
        )}
      </div>
      {canRemove && (
        <button
          onClick={onRemove}
          className="shrink-0 rounded p-1 text-slate-500 hover:text-red-400"
          title="Remove"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}
